import React, { useState, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, Prompt } from 'react-router-dom';
import { map, filter } from 'lodash';

import { useQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { useFlag, useSetContextualLDAttributes } from 'lane-shared/hooks';
import { useScheduleServiceRequestDueDateEnabled } from 'react-hooks';
import routes from 'lane-shared/config/routes';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import { channelModuleByChannelId } from 'lane-shared/graphql/modules';
import { pause } from 'async-timing';
import { convertToUUID, convertTo62 } from 'uuid-encoding';
import { FeatureFlag } from 'constants-flags';
import { ContentTypeEnum } from 'constants-content';
import {
  AttachmentImageContentTypeEnum,
  AttachmentDocumentContentTypeEnum,
  AttachmentVariantEnum,
} from 'lane-shared/types/attachment';
import { toGraphQLContentType } from '../../../../../../../helpers/contentTypeMapper';

import {
  Flex,
  Button,
  ValidationMessage,
  ErrorMessage,
  AddAttachment,
  Tooltip,
} from 'components';
import {
  HookFormInput,
  HookFormDropdown,
  HookFormTextarea,
  HookFormToggle,
  HookFormMultiSelectDropdown,
} from 'components/reactHookForm';
import { P, H4, H5 } from 'components/typography';
import { ActiveChannelTypeEnum } from 'constants-channel';

import { InlineDropdownOption } from '../../constants/InlineDropdownOption';
import {
  CustomQuestions,
  CustomQuestionsType,
} from '../../constants/customQuestions';

import { updateServiceRequestMutation } from 'graphql-queries';

import styles from './ServiceRequestEditForm.scss';
import { AmazonS3Buckets } from 'lane-shared/config';
import { useGetUnitsFieldOptions } from 'domains/workOrder/feature/components/hooks/useGetUnitsFieldOptions';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { ServiceRequestSchedule } from 'domains/workOrder/feature/components/content-blocks/ServiceRequestSchedule';
import { getChannelTimezone } from 'domains/workOrder/serviceRequest/helpers/getChannelTimezone';
import type { AddressType } from 'application-types';

interface ChannelProp {
  slug: string;
  type: ActiveChannelTypeEnum;
  address?: Pick<AddressType, 'geo'>;
}

export interface ServiceRequestEditFormProps {
  channel: ChannelProp;
  serviceRequest: any;
  getServiceRequest: () => void;
}

export function ServiceRequestEditForm({
  channel,
  serviceRequest,
  getServiceRequest,
}: ServiceRequestEditFormProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [navigateAway, setNavigateAway] = useState(true);
  const isEnabledCesLinksForServiceRequests = useFlag(
    FeatureFlag.EnableCesLinksForServiceRequests,
    false
  );
  const isScheduleServiceRequestDueDateEnabled =
    useScheduleServiceRequestDueDateEnabled();

  const srCategory = serviceRequest?.category;

  useSetContextualLDAttributes({
    activeContentOriginChannelId: serviceRequest?.channelId?.id
      ? convertTo62(serviceRequest.channelId.id)
      : undefined,
  });

  const { options: suiteOptions } = useGetUnitsFieldOptions(
    serviceRequest?.channelId?.id,
    !isEnabledCesLinksForServiceRequests
  );

  const { data: moduleResp } = useQuery(channelModuleByChannelId, {
    variables: {
      channelId: serviceRequest?.channelId?.id,
      contentType: toGraphQLContentType(ContentTypeEnum.WorkOrder),
    },
  });
  const isPropertyChannel = channel?.type === ActiveChannelTypeEnum.Property;
  const settings: WorkOrderModuleSettings =
    moduleResp?.channelModuleByChannelId?.settings;
  const selectedCategory = settings?.categories?.find(
    category => category.firendlyName === srCategory
  );
  const categoryOptions: InlineDropdownOption[] = settings?.categories
    ? settings.categories
        .filter(category => category.issues.length > 0)
        .map((category: { firendlyName: string }) => ({
          label: category.firendlyName,
          value: category.firendlyName,
        }))
    : [];
  const locationOptions: InlineDropdownOption[] = settings?.locations
    ? settings.locations.map((locationOption: string) => ({
        label: locationOption,
        value: locationOption,
      }))
    : [];

  const [issueOptions, setIssueOptions] = useState<InlineDropdownOption[]>(
    selectedCategory
      ? selectedCategory.issues.map(issue => ({
          label: issue,
          value: issue,
        }))
      : []
  );

  const customQuestions: CustomQuestions[] = settings?.customQuestions
    ? settings.customQuestions.map((customQuestion: any) => {
        if (
          customQuestion?.validators &&
          customQuestion?.validators[0] &&
          customQuestion?.validators[0]?.value
        ) {
          const options: InlineDropdownOption[] =
            customQuestion.validators[0].value.map((customfield: any) => ({
              label: customfield.value,
              value: customfield.value,
            }));

          return {
            label: customQuestion.friendlyName,
            type: customQuestion.packagedType,
            options,
          };
        }

        return {
          label: customQuestion.friendlyName,
          type: customQuestion.packagedType,
        };
      })
    : [];

  let customQuestionValues = {};
  const deletedCustomQuestions: any[] = [];

  if (serviceRequest?.customQuestions) {
    serviceRequest?.customQuestions.forEach((question: any) => {
      customQuestionValues = {
        ...customQuestionValues,
        [question.Question]: question.Answer,
      };

      if (
        !customQuestions.some(
          (customQuestion: any) => customQuestion.label === question.Question
        )
      ) {
        deletedCustomQuestions.push({
          Question: question.Question,
          Answer: question.Answer,
        });
      }
    });
  }

  const initialFormValues: {
    category: any;
    issue: any;
    location: any;
    description: any;
    suiteIds?: { value: string }[];
    floor?: any;
    suite?: any;
    scheduledDueDate?: Date | null;
  } = {
    category: serviceRequest?.category,
    issue: serviceRequest?.issue,
    location: serviceRequest?.location,
    description: serviceRequest?.description,
    scheduledDueDate: serviceRequest?.scheduledDueDate ?? null,
    ...customQuestionValues,
  };

  if (isEnabledCesLinksForServiceRequests) {
    const filteredSuites = (serviceRequest?.suiteIds || []).filter(
      (suite: { sourceEntityId?: string | null } | null) =>
        suite?.sourceEntityId
    );

    initialFormValues.suiteIds = filteredSuites.map(
      (suite: { sourceEntityId?: string | null } | null) => ({
        value: suite?.sourceEntityId || '',
      })
    );
  } else {
    initialFormValues.floor = serviceRequest?.floor;
    initialFormValues.suite = serviceRequest?.suite;
  }

  const updateServiceRequest = async (fields: any) => {
    try {
      await pause();
      const customAnswers: any[] = [...deletedCustomQuestions];

      customQuestions.forEach((question: any) => {
        if (fields[question.label] !== undefined) {
          let answer = fields[question.label]?.value
            ? fields[question.label].value
            : fields[question.label];

          if (typeof answer === 'boolean') {
            answer = answer ? t`Yes` : t`No`;
          }

          customAnswers.push({
            Question: question.label,
            Answer: answer,
          });
        }

        delete fields[question.label];
      });
      serviceRequest?.customQuestions?.forEach((question: any) => {
        delete fields[question.Question];
      });
      fields.category = fields?.category?.value
        ? fields.category.value
        : serviceRequest?.category;
      fields.issue = fields?.issue?.value
        ? fields.issue.value
        : serviceRequest?.issue;
      fields.location = fields?.location?.value
        ? fields.location.value
        : serviceRequest?.location;

      if (isEnabledCesLinksForServiceRequests) {
        fields.floor = '';
        fields.suite = '';
        fields.suiteIds = map(filter(fields?.suiteIds, 'value'), 'value') || [];
      } else {
        fields = {
          floor: serviceRequest?.floor,
          suite: serviceRequest?.suite,
          suiteIds:
            (serviceRequest?.suiteIds || [])
              .filter((suite: any) => suite?.sourceEntityId)
              .map(
                (suite: { sourceEntityId: string }) => suite.sourceEntityId
              ) || [],
          ...fields,
        };
      }

      await getClient().mutate({
        mutation: updateServiceRequestMutation,
        variables: {
          updateServiceRequest: {
            serviceRequestId: convertToUUID(serviceRequest?.serviceRequestId),
            description: serviceRequest?.description,
            category: serviceRequest?.category,
            issue: serviceRequest?.issue,
            location: serviceRequest?.location,
            company: serviceRequest?.company?.id,
            assignee: serviceRequest?.assignee?.id,
            assigneeGroups: serviceRequest?.assigneeGroups,
            isBillable: serviceRequest?.isBillable,
            notes: serviceRequest?.notes,
            hours: serviceRequest?.hours,
            equipment: serviceRequest?.equipment,
            customQuestions: customAnswers,
            status: serviceRequest?.status,
            scheduledDueDate: fields.scheduledDueDate,
            ...fields,
          },
        },
      });
      getServiceRequest();
      setLoading(false);
      window.Toast.show(t('web.admin.serviceRequest.toast.success'));

      return true;
    } catch (error: unknown) {
      setLoading(false);
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      setError(errorMessage);
    }

    return false;
  };

  const serviceRequestListPath =
    routes.channelAdminWorkOrdersServiceRequests.replace(':id', channel?.slug);

  const { register, handleSubmit, control, formState, setValue, watch } =
    useForm<any>({
      mode: 'all',
      defaultValues: initialFormValues,
    });

  // Validate scheduledDueDate completeness
  const scheduledDueDateValue = watch('scheduledDueDate');
  const isScheduledDueDateValid = useMemo(() => {
    if (!isScheduleServiceRequestDueDateEnabled) {
      return true;
    }

    return scheduledDueDateValue !== undefined;
  }, [scheduledDueDateValue, isScheduleServiceRequestDueDateEnabled]);

  const onCategoryChange = (item: any) => {
    const newCategory = settings?.categories?.find(
      category => category.firendlyName === item.value
    );
    const issueOptionTemp = newCategory
      ? newCategory.issues.map(issue => ({
          label: issue,
          value: issue,
        }))
      : [];

    setValue('issue', '');
    setIssueOptions(issueOptionTemp);
  };

  const renderCustomQuestions = () => {
    return (
      <div>
        {customQuestions.map(
          (customQuestion: CustomQuestions, index: number) => (
            <div className={styles.CustomDataDescription} key={index}>
              {customQuestion.type === CustomQuestionsType.None && (
                <div className={styles.input}>
                  <P className={styles.inputHeadings}>{customQuestion.label}</P>
                  <HookFormInput
                    control={control}
                    name={customQuestion.label}
                    inputClassName={customQuestion.label}
                    testId={customQuestion.label}
                  />
                </div>
              )}
              {customQuestion.type === CustomQuestionsType.Checkboxes && (
                <div className={styles.input}>
                  <P className={styles.inputHeadings}>{customQuestion.label}</P>
                  <HookFormToggle
                    control={control}
                    {...register(customQuestion.label)}
                  />
                </div>
              )}
              {customQuestion.type === CustomQuestionsType.Dropdown && (
                <div className={styles.input}>
                  <P className={styles.inputHeadings}>{customQuestion.label}</P>
                  <HookFormDropdown
                    placeholder={t('web.admin.serviceRequest.select')}
                    items={customQuestion.options || []}
                    control={control}
                    {...register(customQuestion.label)}
                    isFullWidth
                  />
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  const formSubmission = async (data: any) => {
    const isUpdated = await updateServiceRequest(data);

    if (isUpdated) {
      afterSave();
    }
  };

  const afterSave = () => {
    history.push(serviceRequestListPath);
  };

  if (!navigateAway && Object.keys(formState.errors).length > 0) {
    setNavigateAway(true);
  }

  return (
    <>
      {error && (
        <Flex justify="center" className="mt-2">
          <ErrorMessage error={error} />
        </Flex>
      )}
      <div className={styles.serviceRequestForm}>
        <H4>{t('web.admin.serviceRequest.edit')}</H4>
        <form onSubmit={handleSubmit(formSubmission)} style={{ width: '100%' }}>
          <div className={styles.input}>
            <P className={styles.inputHeadings}>
              {t('web.admin.serviceRequest.edit.category.title')}
              <span className={styles.required}>*</span>
            </P>
            <HookFormDropdown
              placeholder={t('web.admin.serviceRequest.select')}
              items={categoryOptions}
              control={control}
              onValueChange={onCategoryChange}
              {...register('category', { required: true })}
              isFullWidth
              testId="categoryDropdown"
            />
            {formState.errors.category && (
              <ValidationMessage
                className={styles.validationMessage}
                errors={[t('web.admin.serviceRequest.field.required')]}
                withoutIcon
              />
            )}
          </div>
          <div className={styles.input}>
            <P className={styles.inputHeadings}>
              {t('web.admin.serviceRequest.edit.issue.title')}
            </P>
            <HookFormDropdown
              placeholder={t('web.admin.serviceRequest.select')}
              items={issueOptions}
              control={control}
              {...register('issue')}
              isFullWidth
              testId="issueDropdown"
            />
            {formState.errors.issue && (
              <ValidationMessage
                className={styles.validationMessage}
                errors={[t('web.admin.serviceRequest.field.required')]}
                withoutIcon
              />
            )}
          </div>
          <div className={styles.input}>
            <P className={styles.inputHeadings}>
              {t('web.admin.serviceRequest.edit.location.title')}
              <span className={styles.required}>*</span>
            </P>
            <HookFormDropdown
              placeholder={t('web.admin.serviceRequest.select')}
              items={locationOptions}
              control={control}
              {...register('location', { required: true })}
              isFullWidth
              testId="locationDropdown"
            />
            {formState.errors.location && (
              <ValidationMessage
                className={styles.validationMessage}
                errors={[t('web.admin.serviceRequest.field.required')]}
                withoutIcon
              />
            )}
          </div>
          <div className={styles.input}>
            <P className={styles.inputHeadings}>
              {t('web.admin.serviceRequest.edit.description.title')}
            </P>
            <HookFormTextarea
              containerClassName={styles.field}
              control={control}
              {...register('description')}
              showLengthIndicator
              minRows={4}
              maxLength={500}
            />
          </div>
          {isPropertyChannel && (
            <>
              {!isEnabledCesLinksForServiceRequests && (
                <>
                  <div className={styles.input}>
                    <P className={styles.inputHeadings}>
                      {t('web.admin.serviceRequest.edit.floor.title')}
                    </P>
                    <HookFormInput
                      control={control}
                      {...register('floor')}
                      inputClassName="floor"
                      className={styles.floorInput}
                    />
                  </div>
                  <div className={styles.input}>
                    <P className={styles.inputHeadings}>
                      {t('web.admin.serviceRequest.edit.suite.title')}
                    </P>
                    <HookFormInput
                      control={control}
                      {...register('suite')}
                      inputClassName="suite"
                      className={styles.suiteInput}
                    />
                  </div>
                </>
              )}

              {isEnabledCesLinksForServiceRequests &&
                suiteOptions.length > 0 && (
                  <div className={styles.input}>
                    <P className={styles.inputHeadings}>
                      {t('web.admin.serviceRequest.edit.suites.title')}
                    </P>
                    <HookFormMultiSelectDropdown
                      control={control}
                      items={suiteOptions}
                      isFullWidth
                      placeholder={t('web.admin.serviceRequest.select')}
                      {...register('suiteIds', {
                        value: initialFormValues.suiteIds,
                      })}
                    />
                  </div>
                )}
            </>
          )}
          {isScheduleServiceRequestDueDateEnabled && (
            <div className={styles.input}>
              <ServiceRequestSchedule
                onChange={schedule =>
                  setValue('scheduledDueDate', schedule.scheduledDueDate, {
                    shouldDirty: true,
                  })
                }
                schedule={{
                  scheduledDueDate: watch('scheduledDueDate'),
                }}
                timeZone={
                  channel?.address
                    ? (getChannelTimezone(channel.address) ?? undefined)
                    : undefined
                }
              />
              {!isScheduledDueDateValid && (
                <div className={styles.error} role="alert">
                  {t('web.admin.serviceRequest.schedule.incomplete')}
                </div>
              )}
            </div>
          )}
          <div className={styles.input}>
            <div className={styles.inputHeadings}>
              <H5>{t`web.admin.serviceRequest.form.attachment.heading`}</H5>
            </div>
            <div className={styles.ServiceRequestAttachmentsDisclaimer}>
              <span>{t`web.admin.serviceRequest.form.attachment.disclaimer`}</span>
            </div>
            <div className={styles.fieldAttachments}>
              {serviceRequest?.attachmentRefId && (
                <AddAttachment
                  entityId={serviceRequest?.attachmentRefId}
                  entityType={ContentTypeEnum.WorkOrder}
                  editMode={false}
                  variant={AttachmentVariantEnum.WorkOrder}
                  canAddAttachments
                  acceptedFileTypes={[
                    ...Object.values(AttachmentImageContentTypeEnum),
                    AttachmentDocumentContentTypeEnum.pdf,
                  ]}
                  s3Bucket={AmazonS3Buckets.Activate}
                />
              )}
            </div>
          </div>
          {deletedCustomQuestions.length > 0 &&
            deletedCustomQuestions.map((customQuestion: any, index: number) => (
              <Flex
                className={styles.customQuestionDataRow}
                width="full"
                key={index}
              >
                <span>
                  {customQuestion.Question}
                  <Tooltip
                    TooltipComponent={t(
                      'web.admin.serviceRequest.customQuestions.archived.tooltip'
                    )}
                    placement="top"
                  >
                    <Icon
                      name="info-circle"
                      className={styles.infoIcon}
                      set={ICON_SET_FONTAWESOME}
                      type="far"
                    />
                  </Tooltip>
                </span>
                <P>{customQuestion.Answer}</P>
              </Flex>
            ))}
          {customQuestions.length > 0 && renderCustomQuestions()}
          <Flex className={styles.buttonGroup}>
            <Button
              variant="outlined"
              size="large"
              testId="cancelFormButton"
              fullWidth
              onClick={() => {
                setNavigateAway(false);
                history.goBack();
              }}
              className={styles.formButton}
              labelClassName="cancel-button"
            >
              {t('web.admin.serviceRequest.cancel.button')}
            </Button>
            <Button
              type="submit"
              variant="activate-contained"
              size="large"
              fullWidth
              testId="submitFormButton"
              loading={loading}
              disabled={loading || !isScheduledDueDateValid}
              onClick={() => setNavigateAway(false)}
              className={styles.formButton}
              labelClassName="submit-button"
            >
              {t('web.admin.serviceRequest.save')}
            </Button>
          </Flex>
          {navigateAway && (
            <Prompt
              when={formState.isDirty}
              message={t(
                'web.admin.serviceRequest.equipment.form.navigation.prompt'
              )}
            />
          )}
        </form>
      </div>
    </>
  );
}
